import React from 'react';

import { daysAt, fmtDays, rangeIndexForTrade } from './ranges';
import { activePresetForUi, cardById, exactMatch, suggestedPresetForUi } from './resolve';
import { TatStyles as S } from './styles';

/**
 * B1–B3 — preset ladder at the top of a multi-preset trade.
 * Selected = explicit preset click. Suggested = inferred from ticked scopes.
 */
const PresetBlock = ({
  cfg,
  resolved,
  picks,
  chosenPresetId,
  typicalStdIdx,
  onPickPreset,
  onJumpToTat,
  readOnly,
  blockRef
}) => {
  if (!cfg || cfg.presets.length <= 1) return null;

  const colIdx = rangeIndexForTrade(cfg, typicalStdIdx);
  const uiActive = activePresetForUi(cfg, picks, chosenPresetId, resolved?.preset);
  const suggested = suggestedPresetForUi(cfg, picks, chosenPresetId);
  let status = 'Nothing picked yet — choose a preset or tick items below.';
  if (uiActive && suggested && String(uiActive.id) !== String(suggested.id)) {
    status = `Selected “${uiActive.name}” — suggested “${suggested.name}” from extra picks.`;
  } else if (uiActive) {
    if (exactMatch(cfg, uiActive, picks)) {
      status = `Your picks match “${uiActive.name}”.`;
    } else if (resolved && !resolved.exact) {
      status = `Customised from “${resolved.preset.name}” — still on the ${resolved.card.name} card.`;
    } else {
      status = `On “${uiActive.name}”.`;
    }
  } else if (suggested) {
    status = `Suggested “${suggested.name}” from your picks — select it to confirm, or keep ticking.`;
  }

  return (
    <S.PresetCard ref={blockRef}>
      <S.PresetHead>
        <S.PresetHeadCopy>
          <S.PresetTitle>Start from a scope preset</S.PresetTitle>
          <S.PresetSub>
            Pick one and it ticks the matching items below. Each preset maps to a turnaround card
            {onJumpToTat ? (
              <>
                {' '}
                —{' '}
                <S.PresetJump type='button' onClick={onJumpToTat}>
                  see the impact at the end of this trade ↓
                </S.PresetJump>
              </>
            ) : null}
            .
          </S.PresetSub>
        </S.PresetHeadCopy>
        <S.PresetStatus>{status}</S.PresetStatus>
      </S.PresetHead>

      <S.PresetGrid role='radiogroup' aria-label='Scope presets'>
        {cfg.presets.map((p, i) => {
          const isSelected = String(uiActive?.id) === String(p.id);
          const isSuggested = !isSelected && String(suggested?.id) === String(p.id);
          const cust = !!(resolved && !resolved.exact && isSelected);
          const pCard = cardById(cfg, p.cardId);
          const pDays = daysAt(pCard, colIdx, { side: 'customer' });
          const daysText =
            colIdx == null
              ? 'Days once you pick a sheet range'
              : pDays == null
                ? 'Quoted on acceptance'
                : `${fmtDays(pDays)} at ${cfg.ranges[colIdx]} sheets`;
          const stateLabel = isSelected ? 'Selected' : isSuggested ? 'Suggested' : 'Select';
          return (
            <S.PresetTile
              key={p.id}
              type='button'
              role='radio'
              aria-checked={isSelected}
              $matched={isSelected}
              $suggested={isSuggested}
              $custom={cust}
              disabled={readOnly}
              onClick={() => !readOnly && onPickPreset && onPickPreset(p)}>
              <S.PresetTileTop>
                <S.PresetN>Preset {i + 1}</S.PresetN>
                <S.PresetState $matched={isSelected} $suggested={isSuggested} $custom={cust}>
                  {stateLabel}
                </S.PresetState>
              </S.PresetTileTop>
              <S.PresetName>{p.name}</S.PresetName>
              {p.desc ? <S.PresetDesc>{p.desc}</S.PresetDesc> : null}
              <S.PresetMeta>
                <span>Card: {pCard?.name || '—'}</span>
                <S.PresetDays $matched={isSelected || isSuggested}>{daysText}</S.PresetDays>
              </S.PresetMeta>
            </S.PresetTile>
          );
        })}
      </S.PresetGrid>
    </S.PresetCard>
  );
};

export default PresetBlock;
